/**
 * @file IntegrationCards Container Component
 * @description Main container component that displays integration cards and manages the
 * multi-step integration wizard modal. Handles the complete flow from card selection
 * through integration creation.
 */

import { useAtomValue } from 'jotai';
import React, { useState, useCallback } from 'react';
import { Row, Col, Container } from 'reactstrap';
import { companyIdAtom } from 'views/state';

import IntegrationCard from './IntegrationCard';
import StepperModal, { StepConfig } from './StepperModal';
import './styles.css';
import IntegrationStepsConfig from './StepperModal/config';
import {
  IntegrationModule,
  CloudProvider,
  CLOUD_PROVIDER_CONFIG,
  INTEGRATION_MODULE_CONFIG,
  IntegrationData,
} from './types';

export { IntegrationModule, CloudProvider } from './types';

/**
 * IntegrationCards - Container component for cloud integration management
 *
 * This component serves as the main entry point for the cloud integration feature.
 * It displays a grid of integration cards (e.g., AWS CSPM, AWS CIEM) and manages
 * the multi-step wizard modal for creating new integrations.
 *
 * Flow:
 * 1. User clicks on an integration card (e.g., AWS CSPM)
 * 2. Modal opens with multi-step wizard
 * 3. User completes wizard steps (account details, credentials, review)
 * 4. Integration is created via API
 * 5. Event is dispatched for parent components to refresh
 *
 * State Management:
 * - modalOpen: Controls modal visibility
 * - selectedCloudProvider: Tracks selected cloud provider (AWS, Azure, etc.)
 * - selectedIntegrationModule: Tracks selected module (CSPM, CIEM)
 * - companyId: Retrieved from global state for API calls
 *
 * @component
 * @returns {React.ReactElement} Grid of integration cards with wizard modal
 */
const IntegrationCards = () => {
  // Modal state management
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCloudProvider, setSelectedCloudProvider] =
    useState<CloudProvider | null>(null);
  const [selectedIntegrationModule, setSelectedIntegrationModule] =
    useState<IntegrationModule | null>(null);

  // Get company ID from global state for API calls
  const companyId = useAtomValue(companyIdAtom);

  /**
   * Handles integration card click
   * Opens the wizard modal with selected cloud provider and module
   * @param {IntegrationModule} integrationType - Selected integration module (CSPM/CIEM)
   * @param {CloudProvider} cloudProvider - Selected cloud provider (AWS, Azure, etc.)
   */
  const onClickHandler = useCallback(
    (integrationType: IntegrationModule, cloudProvider: CloudProvider) => {
      setSelectedIntegrationModule(integrationType);
      setSelectedCloudProvider(cloudProvider);
      setModalOpen(true); // Open the wizard modal
    },
    [],
  );

  /**
   * Retrieves the appropriate step configurations for the wizard
   * Different cloud providers and modules may have different wizard steps
   * @param {CloudProvider} cloudProvider - The cloud provider
   * @param {IntegrationModule} integrationType - The integration module
   * @returns {StepConfig[]} Array of step configurations for the wizard
   */
  const getStepConfigs = useCallback(
    (
      cloudProvider: CloudProvider,
      integrationType: IntegrationModule,
    ): StepConfig[] => {
      return IntegrationStepsConfig(cloudProvider, integrationType, companyId);
    },
    [companyId],
  );

  /**
   * Handles modal close event
   * Resets all selection state when modal is closed
   */
  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setSelectedIntegrationModule(null);
    setSelectedCloudProvider(null);
  }, []);

  /**
   * Handles successful integration creation
   * Dispatches a custom event that parent components can listen to
   * for refreshing the account listing
   * @param {IntegrationData} data - The completed integration data
   */
  const handleIntegrationComplete = useCallback((data: IntegrationData) => {
    // Dispatch event to trigger account listing refresh
    window.dispatchEvent(
      new CustomEvent('integration-created', { detail: data }),
    );
  }, []);

  return (
    <>
      {/* Integration Cards Grid */}
      <Container className="d-flex justify-content-center w-100">
        <Row className="d-flex justify-content-center align-self-center">
          {/* AWS CSPM Integration Card */}
          <Col className="p-0">
            <IntegrationCard
              cloudProvider={CloudProvider.AWS}
              integrationModule={IntegrationModule.CSPM}
              onClick={() =>
                onClickHandler(IntegrationModule.CSPM, CloudProvider.AWS)
              }
            />
          </Col>
          {/* AWS CIEM Integration Card */}
          <Col className="p-0">
            <IntegrationCard
              cloudProvider={CloudProvider.AWS}
              integrationModule={IntegrationModule.CIEM}
              onClick={() =>
                onClickHandler(IntegrationModule.CIEM, CloudProvider.AWS)
              }
            />
          </Col>
        </Row>
      </Container>

      {/* Multi-Step Integration Wizard Modal */}
      {modalOpen && selectedIntegrationModule && selectedCloudProvider && (
        <StepperModal
          title={`Create ${CLOUD_PROVIDER_CONFIG[selectedCloudProvider].label} ${INTEGRATION_MODULE_CONFIG[selectedIntegrationModule].fullName} Integration`}
          steps={getStepConfigs(
            selectedCloudProvider,
            selectedIntegrationModule,
          )}
          onClose={handleModalClose}
          onComplete={handleIntegrationComplete}
          initialData={{
            integrationModule: selectedIntegrationModule,
            cloudProvider: selectedCloudProvider,
          }}
        />
      )}
    </>
  );
};

export default IntegrationCards;
